import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { authAPI, ticketAPI } from '@/lib/api';
import { Plus, LogOut, Clock, CheckCircle2, AlertTriangle, ClipboardList, ArrowRight, MessageSquare } from 'lucide-react';
import CreateTicketDialog from '@/components/CreateTicketDialog';
import FeedbackPopup from '@/components/FeedbackPopup';
import TicketCard from '@/components/TicketCard';
import InterfaceSwitcher from '@/components/InterfaceSwitcher';
import { POLLING_INTERVALS } from '@/lib/constants';

const Dashboard = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [ticketsError, setTicketsError] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [feedbackTicket, setFeedbackTicket] = useState(null);
  const [pendingFeedbackCount, setPendingFeedbackCount] = useState(0);
  const shownFeedbackIdsRef = useRef(new Set());
  const feedbackTimerRef = useRef(null);

  const loadData = useCallback(async () => {
    // Smooth UX: keep the loading spinner visible for a moment so the transition
    // from sign-in → dashboard feels intentional (no abrupt flicker).
    const minDelay = new Promise((resolve) => setTimeout(resolve, 500));

    // Promise.allSettled keeps each fetch independent — if /tickets times
    // out, /auth/me still resolves so the InterfaceSwitcher can render
    // and the user can navigate elsewhere without refreshing the page.
    const [userSettled, ticketsSettled, pendingSettled] = await Promise.allSettled([
      authAPI.getMe(),
      ticketAPI.getTickets(),
      ticketAPI.getPendingFeedback(),
    ]);
    await minDelay;

    if (userSettled.status === 'fulfilled') {
      setUser(userSettled.value.data);
    } else if (process.env.NODE_ENV !== 'production') {
      console.debug('Dashboard /auth/me failed:', userSettled.reason?.message);
    }

    if (ticketsSettled.status === 'fulfilled') {
      // Defensively strip "Ticket Closed" — archival-only records owned by
      // the admin panel. They must never appear on TC UI-1 regardless of
      // which role fetched them (backend already hides them for non-admins;
      // this also protects admin users who switch into the TC interface
      // via the Interface Switcher and guards against stale cache).
      setTickets(
        (ticketsSettled.value.data || []).filter((t) => t.status !== 'Ticket Closed'),
      );
      setTicketsError(false);
    } else {
      // Keep the last known tickets on screen; polling retries automatically.
      setTicketsError(true);
      if (process.env.NODE_ENV !== 'production') {
        console.debug('Dashboard /tickets failed:', ticketsSettled.reason?.message);
      }
    }

    // Pending feedback is best-effort — its absence shouldn't block the
    // main dashboard render.
    const pending = pendingSettled.status === 'fulfilled'
      ? (pendingSettled.value.data?.tickets || [])
      : [];
    setPendingFeedbackCount(pending.length);
    const nextCandidate = pending.find((t) => !shownFeedbackIdsRef.current.has(t.ticket_id));
    if (nextCandidate && !feedbackTicket) {
      if (feedbackTimerRef.current) clearTimeout(feedbackTimerRef.current);
      feedbackTimerRef.current = setTimeout(() => {
        shownFeedbackIdsRef.current.add(nextCandidate.ticket_id);
        setFeedbackTicket(nextCandidate);
      }, 3000);
    }
    setLoading(false);
  }, [feedbackTicket]);

  useEffect(() => {
    loadData();
    
    // Polling for dashboard refresh
    const interval = setInterval(loadData, POLLING_INTERVALS.DASHBOARD_REFRESH);
    return () => {
      clearInterval(interval);
      if (feedbackTimerRef.current) clearTimeout(feedbackTimerRef.current);
    };
  }, [loadData]);

  const handleLogout = async () => {
    try {
      await authAPI.logout();
      navigate('/');
    } catch (error) {
      toast.error('Logout failed');
    }
  };

  // Categorize tickets. Backend already excludes Resolved / Completed /
  // Ticket Closed from the list it returns for creators, but we defensively
  // re-filter on the client so the main "My Tickets" feed never shows
  // resolved or archival-closed items regardless of cache freshness or
  // service-worker staleness.
  const TERMINAL_STATUSES = ['Resolved', 'Completed', 'Closed - Feedback Received', 'Ticket Closed'];
  const closedTickets = tickets.filter(t =>
    TERMINAL_STATUSES.includes(t.status) || t.feedback_submitted_at
  );
  const activeTickets = tickets.filter(t =>
    !TERMINAL_STATUSES.includes(t.status) && !t.feedback_submitted_at
  );
  const breachedTickets = tickets.filter(t => t.status === 'SLA BREACHED');

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="w-12 h-12 border-4 border-[#E97832] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10 backdrop-blur-lg bg-white/80">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 sm:py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3 sm:gap-4 min-w-0">
              <img 
                src="https://customer-assets.emergentagent.com/job_gurukul-care-desk/artifacts/kp1n4cqn_VGWS-NEW%20LOGO%20%281%29.png" 
                alt="VGWS Logo" 
                className="h-8 sm:h-10 object-contain flex-shrink-0"
              />
              <div className="min-w-0">
                <h1 className="text-base sm:text-xl font-heading font-bold text-slate-900 truncate">Care Desk</h1>
                <p className="text-xs sm:text-sm text-slate-600 truncate">{user?.name}</p>
              </div>
            </div>
            <div className="flex items-center gap-1 sm:gap-3 flex-shrink-0">
              <InterfaceSwitcher user={user} currentInterface="ticket_creator" />
              <Button
                data-testid="fb-nav-btn"
                variant="outline"
                size="sm"
                onClick={() => navigate('/feedback')}
                className="relative flex items-center gap-1 sm:gap-2 border-[#E97832]/40 text-[#E97832] hover:bg-[#E97832]/10"
              >
                <MessageSquare className="w-4 h-4" />
                <span className="hidden sm:inline">Feedback</span>
                {pendingFeedbackCount > 0 && (
                  <Badge
                    className="absolute -top-1.5 -right-1.5 bg-red-600 text-white px-1.5 min-w-[1.25rem] h-5 text-xs rounded-full"
                    data-testid="fb-nav-badge"
                  >
                    {pendingFeedbackCount}
                  </Badge>
                )}
              </Button>
              <Button 
                data-testid="logout-btn"
                variant="ghost" 
                onClick={handleLogout} 
                size="sm"
                className="flex items-center gap-1 sm:gap-2"
              >
                <LogOut className="w-4 h-4" />
                <span className="hidden sm:inline">Logout</span>
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Staff Resolver Access Banner - Only shown for registered staff */}
      {(user?.is_registered_staff || user?.can_switch_interface) && (
        <div className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2.5 sm:py-3">
            <div className="flex items-center justify-between flex-wrap gap-2 sm:gap-3">
              <div className="flex items-center gap-2 sm:gap-3 min-w-0">
                <ClipboardList className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" />
                <div className="min-w-0">
                  <p className="font-medium text-sm sm:text-base truncate">
                    {user?.staff_details?.designation || 'Ticket Resolver'}
                  </p>
                  <p className="text-xs sm:text-sm text-emerald-100 hidden sm:block">
                    You have tickets allocated to you. Go to your Resolver Dashboard to manage them.
                  </p>
                </div>
              </div>
              <Button
                data-testid="go-to-resolver-dashboard-btn"
                onClick={() => navigate('/process-owner')}
                size="sm"
                className="bg-white text-emerald-700 hover:bg-emerald-50 font-semibold shadow-md text-xs sm:text-sm flex-shrink-0"
              >
                <span>My Tickets</span>
                <ArrowRight className="w-3 h-3 sm:w-4 sm:h-4 ml-1 sm:ml-2" />
              </Button>
            </div>
          </div>
        </div>
      )}

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
        {ticketsError && tickets.length === 0 && (
          <div data-testid="tickets-load-error" className="mb-4 flex flex-col sm:flex-row sm:items-center gap-3 justify-between rounded-lg border border-amber-300 bg-amber-50 px-4 py-3">
            <p className="text-sm text-amber-800 font-medium">
              Your tickets are taking longer than usual to load. We'll keep retrying automatically.
            </p>
            <Button
              size="sm"
              variant="outline"
              onClick={loadData}
              data-testid="tickets-retry-btn"
              className="border-amber-400 text-amber-800 hover:bg-amber-100 flex-shrink-0"
            >
              Retry now
            </Button>
          </div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-4 sm:gap-6">
          <Card className="col-span-full md:col-span-8 row-span-2 border-2 border-[#E97832]/20 shadow-lg">
            <CardHeader>
              <CardTitle className="text-2xl font-heading">Submit New Complaint</CardTitle>
              <CardDescription>Report facility issues and track their resolution</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="bg-gradient-to-br from-slate-50 to-white rounded-lg p-6 border border-slate-200">
                  <h3 className="font-heading font-semibold text-lg mb-3">Quick Access Departments</h3>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {['Electrical Dept', 'Plumbing Dept', 'Refractory / Food', 'Clean Tech Dept', 'IT Dept', 'Transportation'].map((dept) => (
                      <button
                        key={dept}
                        data-testid={`dept-quick-${dept.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                        onClick={() => setShowCreateDialog(true)}
                        className="p-3 bg-white border border-slate-200 rounded-md hover:border-[#E97832] hover:shadow-md transition-all text-sm text-left font-medium text-slate-700 hover:text-[#E97832]"
                      >
                        {dept}
                      </button>
                    ))}
                  </div>
                </div>
                <Button 
                  data-testid="create-ticket-btn"
                  onClick={() => setShowCreateDialog(true)} 
                  className="w-full h-12 bg-gradient-to-r from-[#E97832] to-[#EA580C] hover:from-[#C25E20] hover:to-[#C2410C] text-white font-medium shadow-md"
                >
                  <Plus className="w-5 h-5 mr-2" />
                  Create New Ticket
                </Button>
              </div>
            </CardContent>
          </Card>

          <div className="col-span-full md:col-span-4 space-y-4">
            <Card data-testid="stats-active" className="bg-gradient-to-br from-blue-50 to-white border-blue-200">
              <CardContent className="p-4 sm:pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs sm:text-sm font-medium text-slate-600">Active Tickets</p>
                    <p className="text-2xl sm:text-3xl font-bold text-blue-600 mt-1">{activeTickets.length}</p>
                  </div>
                  <Clock className="w-8 h-8 sm:w-10 sm:h-10 text-blue-500 opacity-50" />
                </div>
              </CardContent>
            </Card>

            <Card data-testid="stats-closed" className="bg-gradient-to-br from-green-50 to-white border-green-200">
              <CardContent className="p-4 sm:pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs sm:text-sm font-medium text-slate-600">Closed</p>
                    <p className="text-2xl sm:text-3xl font-bold text-green-600 mt-1">{closedTickets.length}</p>
                  </div>
                  <CheckCircle2 className="w-8 h-8 sm:w-10 sm:h-10 text-green-500 opacity-50" />
                </div>
              </CardContent>
            </Card>

            {breachedTickets.length > 0 && (
              <Card data-testid="stats-breached" className="bg-gradient-to-br from-red-50 to-white border-red-200">
                <CardContent className="p-4 sm:pt-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs sm:text-sm font-medium text-slate-600">SLA Breached</p>
                      <p className="text-2xl sm:text-3xl font-bold text-red-600 mt-1">{breachedTickets.length}</p>
                    </div>
                    <AlertTriangle className="w-8 h-8 sm:w-10 sm:h-10 text-red-500 opacity-50" />
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          <div className="col-span-full">
            <Card>
              <CardHeader>
                <CardTitle className="font-heading">My Tickets</CardTitle>
                <CardDescription>Track the status of your submitted complaints</CardDescription>
              </CardHeader>
              <CardContent>
                {tickets.length === 0 ? (
                  <div className="text-center py-12 text-slate-500">
                    <p>No active tickets</p>
                    <p className="text-sm mt-1">Create your first ticket to get started</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {/* Active only — Resolved / Closed tickets are hidden from the
                        creator feed per product spec; analytics retain them. */}
                    {activeTickets.map((ticket) => (
                      <TicketCard key={ticket.ticket_id} ticket={ticket} onClick={() => navigate(`/tickets/${ticket.ticket_id}`)} />
                    ))}
                    {activeTickets.length === 0 && (
                      <p className="text-center text-sm text-slate-500 py-6">
                        All your tickets are resolved. Create a new one anytime.
                      </p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </main>

      <CreateTicketDialog 
        open={showCreateDialog} 
        onClose={() => setShowCreateDialog(false)} 
        onSuccess={() => {
          setShowCreateDialog(false);
          loadData();
        }}
        user={user}
      />

      <FeedbackPopup
        open={!!feedbackTicket}
        ticket={feedbackTicket}
        onClose={() => setFeedbackTicket(null)}
        onSubmitted={() => {
          setFeedbackTicket(null);
          loadData();
        }}
      />
    </div>
  );
};

export default Dashboard;
